import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck, FileWarning, AlertTriangle, Info, X, BellRing } from 'lucide-react';
import { notificationsAPI, pushAPI } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const TYPE_ICONS = {
  invoice_overdue: FileWarning,
  integrity_alert: AlertTriangle,
  info: Info,
};

const TYPE_COLORS = {
  invoice_overdue: 'text-amber-600 bg-amber-50',
  integrity_alert: 'text-red-600 bg-red-50',
  info: 'text-blue-600 bg-blue-50',
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsAPI.list();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  // Check push subscription status
  useEffect(() => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.ready.then(reg => {
        reg.pushManager.getSubscription().then(sub => {
          setPushEnabled(!!sub);
        });
      });
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkRead = async (id) => {
    await notificationsAPI.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await notificationsAPI.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleClick = (notif) => {
    if (!notif.read) handleMarkRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const togglePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    setPushLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      if (pushEnabled) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await pushAPI.unsubscribe({ endpoint: sub.endpoint, keys: { p256dh: '', auth: '' } });
          await sub.unsubscribe();
        }
        setPushEnabled(false);
      } else {
        const keyRes = await pushAPI.getVapidKey();
        const vapidKey = keyRes.data.public_key;
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        const subJson = sub.toJSON();
        await pushAPI.subscribe({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
        });
        setPushEnabled(true);
      }
    } catch (err) {
      console.error('Push toggle error:', err);
    } finally {
      setPushLoading(false);
    }
  };

  const timeAgo = (dateStr) => {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const showPushToggle = 'serviceWorker' in navigator && 'PushManager' in window;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-full hover:bg-stone-100 flex items-center justify-center transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-[18px] h-[18px] text-stone-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 bg-[#D94E18] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <h3 className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} className="text-[11px] text-[#D94E18] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3.5 h-3.5" /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-full hover:bg-stone-100 flex items-center justify-center">
                <X className="w-3.5 h-3.5 text-stone-400" />
              </button>
            </div>
          </div>

          {/* Push notification toggle */}
          {showPushToggle && (
            <div className="flex items-center justify-between px-4 py-2 border-b border-stone-100 bg-stone-50/50">
              <div className="flex items-center gap-2">
                <BellRing className="w-3.5 h-3.5 text-stone-500" />
                <span className="text-[11px] text-stone-600">Push notifications</span>
              </div>
              <button
                onClick={togglePush}
                disabled={pushLoading}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${pushEnabled ? 'bg-[#D94E18]' : 'bg-stone-300'}`}
                data-testid="push-toggle"
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${pushEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </button>
            </div>
          )}

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-stone-200 mx-auto mb-2" />
                <p className="text-xs text-stone-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Info;
                const colorClass = TYPE_COLORS[n.type] || TYPE_COLORS.info;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-stone-50 transition-colors border-b border-stone-50 ${!n.read ? 'bg-orange-50/30' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs leading-tight ${!n.read ? 'font-semibold text-stone-900' : 'text-stone-600'}`}>{n.title}</p>
                        <span className="text-[10px] text-stone-400 flex-shrink-0">{timeAgo(n.created_at)}</span>
                      </div>
                      <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{n.message}</p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-[#D94E18] flex-shrink-0 mt-1.5" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
